import { useEffect, useMemo, useRef, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Sparkles, Send, Printer, FileDown, Trash2, Save, History, Wand2,
  Building2, Filter, Brain, FileText, BookOpen, Bookmark,
} from "lucide-react";
import { toast } from "sonner";
import { BRL, fmtDate, fmtDateTime } from "@/lib/format";

const COLUMN_LABELS = {
  matricula: "Matrícula",
  nome: "Nome",
  cargo: "Cargo",
  nivel: "Nível",
  salario: "Salário",
  data_admissao: "Admissão",
  status: "Status",
  turno: "Turno",
  centro_custo: "Centro de custo",
  sector_name: "Setor",
  company_name: "Empresa",
  name: "Nome",
  code: "Código",
  unidade: "Unidade",
  responsavel: "Responsável",
  quadro_aprovado: "Quadro aprovado",
  quadro_atual: "Quadro atual",
  ocupacao_pct: "% Ocupação",
  diferenca: "Diferença",
  title: "Título",
  quantidade: "Qtde",
  salario_previsto: "Salário previsto",
  urgencia: "Urgência",
  created_at: "Criado em",
  updated_at: "Atualizado em",
  data_desligamento: "Desligamento",
  motivo_desligamento: "Motivo",
  tipo_desligamento: "Tipo",
  tipo: "Tipo",
  carga_horaria: "Carga horária",
  instrutor: "Instrutor",
  modalidade: "Modalidade",
  funcao: "Função",
  cargo_base: "Cargo base",
  salario_base: "Salário base",
  percentual: "% entre níveis",
  estrutura: "Estrutura",
  versao: "Versão",
  ativo: "Ativo",
  hora_entrada: "Entrada",
  hora_saida: "Saída",
  dias_trabalhados: "Dias trab.",
  dias_folga: "Folga",
  carga_semanal: "Carga semanal",
};

const MONEY_COLS = new Set(["salario", "salario_base", "salario_previsto"]);
const DATE_COLS = new Set(["data_admissao", "data_desligamento", "created_at", "updated_at"]);
const PCT_COLS = new Set(["ocupacao_pct", "percentual"]);

function formatCell(col, val) {
  if (val === null || val === undefined || val === "") return "—";
  if (MONEY_COLS.has(col)) return BRL(Number(val));
  if (DATE_COLS.has(col)) return fmtDate(val);
  if (PCT_COLS.has(col)) return `${Number(val).toFixed(1)}%`;
  if (col === "ativo") return val ? "Sim" : "Não";
  if (typeof val === "boolean") return val ? "Sim" : "Não";
  if (Array.isArray(val)) return val.length ? `${val.length} item(s)` : "—";
  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
}

export default function AiReportsPage() {
  const { user } = useAuth();
  const canExport = user?.role !== "consulta";
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [narrative, setNarrative] = useState(true);
  const previewRef = useRef(null);

  useEffect(() => {
    api.get("/ai-reports/templates").then(({ data }) => setTemplates(data.templates || []));
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/ai-reports/history");
      setHistory(data.items || []);
    } catch (err) {
      // Non-fatal: history is auxiliary. Log for devs without disturbing UX.
      console.warn("Falha ao carregar histórico de relatórios IA:", err);
    }
  };

  const generate = async (text) => {
    const p = (text ?? prompt).trim();
    if (!p) { toast.error("Descreva o relatório desejado"); return; }
    setLoading(true);
    setReport(null);
    try {
      const { data } = await api.post("/ai-reports/generate", { prompt: p, narrative });
      setReport(data);
      toast.success(`Relatório gerado: ${data.result.total} registros`);
      loadHistory();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const clear = () => { setPrompt(""); setReport(null); };

  const doPrint = () => {
    if (!report) return;
    window.print();
  };

  const exportExcel = async () => {
    if (!report) return;
    try {
      const r = await api.post("/ai-reports/export-excel", {
        title: report.spec.titulo || "Relatorio",
        columns: report.result.columns,
        rows: report.result.rows,
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      const safe = (report.spec.titulo || "relatorio").replace(/[^\w-]+/g, "_");
      link.download = `${safe}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Falha ao exportar"); }
  };

  const openFromHistory = async (rid) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ai-reports/${rid}`);
      setReport({ ...data, narrative: "" });
      setPrompt(history.find((h) => h.id === rid)?.prompt || "");
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const filters = report?.result?.applied_filters || {};
  const spec = report?.spec || {};
  const rows = report?.result?.rows || [];
  const cols = report?.result?.columns || [];
  const stats = report?.stats || {};

  const appliedChips = useMemo(() => {
    const arr = [];
    if (filters.empresa) arr.push({ icon: Building2, label: "Empresa", value: filters.empresa });
    if (filters.setor) arr.push({ icon: Filter, label: "Setor", value: filters.setor });
    if (filters.status) arr.push({ icon: Filter, label: "Status", value: filters.status });
    if (filters.cargo) arr.push({ icon: Filter, label: "Cargo", value: filters.cargo });
    if (filters.turno) arr.push({ icon: Filter, label: "Turno", value: filters.turno });
    if (filters.periodo?.inicio || filters.periodo?.fim)
      arr.push({ icon: Filter, label: "Período", value: `${filters.periodo?.inicio || "…"} → ${filters.periodo?.fim || "…"}` });
    return arr;
  }, [filters]);

  return (
    <div className="space-y-6" data-testid="ai-reports-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3 no-print">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Central Inteligente
          </div>
          <h1 className="font-display text-3xl font-semibold">Relatórios com IA</h1>
          <p className="mt-1 text-[13.5px] text-muted-foreground">
            Descreva em português o relatório que você precisa. A IA interpreta, consulta o banco e monta a visualização.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        {/* LEFT: prompt + preview */}
        <div className="space-y-5">
          {/* Prompt box */}
          <div className="rounded-lg border border-border bg-white p-5 space-y-3 no-print">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground font-medium flex items-center gap-1">
              <Brain className="h-3 w-3 text-[#234935]" /> Descreva o relatório que você deseja gerar
            </div>
            <Textarea
              rows={3}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder='Ex: "Quero um relatório dos colaboradores ativos da Oficina de Tratores com nome, matrícula, cargo, salário e data de admissão"'
              className="resize-none text-[14px]"
              data-testid="ai-prompt"
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === "Enter") generate();
              }}
            />
            <div className="flex items-center justify-between flex-wrap gap-2">
              <label className="flex items-center gap-2 text-[12px] text-muted-foreground cursor-pointer">
                <input type="checkbox" checked={narrative} onChange={(e) => setNarrative(e.target.checked)} data-testid="ai-narrative-toggle" />
                Incluir análise narrativa executiva
              </label>
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={clear} data-testid="ai-clear"><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Limpar</Button>
                <Button onClick={() => generate()} disabled={loading}
                        className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="ai-generate-btn">
                  <Send className="h-3.5 w-3.5 mr-1.5" /> {loading ? "Gerando..." : "Gerar Relatório"}
                </Button>
              </div>
            </div>
          </div>

          {/* Quick templates */}
          {!report && (
            <div className="rounded-lg border border-border bg-white p-5 no-print">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground font-medium flex items-center gap-1">
                <Wand2 className="h-3 w-3 text-[#C5A059]" /> Modelos rápidos · um clique
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                {templates.map((t) => (
                  <button
                    key={t.key}
                    data-testid={`tpl-${t.key}`}
                    onClick={() => { setPrompt(t.prompt); generate(t.prompt); }}
                    className="rounded-md border border-border bg-white px-3 py-2.5 text-left text-[13px] hover:border-[#234935] hover:bg-[#234935]/[0.03] transition-colors duration-200"
                  >
                    <div className="font-medium">{t.label}</div>
                    <div className="text-[11.5px] text-muted-foreground truncate">{t.prompt}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="rounded-lg border border-border bg-white p-6 no-print">
              <div className="flex items-center gap-3 text-[#234935]">
                <span className="h-3 w-3 animate-ping rounded-full bg-[#C5A059]" />
                <span className="font-display text-sm uppercase tracking-wider">Processando seu pedido com IA...</span>
              </div>
              <div className="mt-4 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-24 w-full" />
              </div>
            </div>
          )}

          {/* Report preview */}
          {report && (
            <div ref={previewRef} className="rounded-lg border border-border bg-white p-6 space-y-4 print-area">
              {/* Print header (visible only when printing) */}
              <div className="hidden print:block mb-4 pb-3 border-b border-black/20">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest">Grupo ALCON</div>
                    <h1 className="text-xl font-bold mt-0.5">TheoKratos RH</h1>
                  </div>
                  <div className="text-right text-[11px]">
                    <div>Emitido em {fmtDateTime(new Date().toISOString())}</div>
                    <div>Por {user?.name} · {user?.role_label}</div>
                  </div>
                </div>
              </div>

              {/* Toolbar */}
              <div className="flex items-center justify-between flex-wrap gap-2 no-print">
                <div>
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 mb-2">
                    <Sparkles className="h-3 w-3 mr-1" /> IA · Claude Sonnet 4.5
                  </Badge>
                  <h2 className="font-display text-2xl font-semibold">{spec.titulo || "Relatório"}</h2>
                  <div className="text-[12.5px] text-muted-foreground">
                    {report.result.entity_label} · {report.result.total} registros
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={doPrint} data-testid="ai-print-btn">
                    <Printer className="h-3.5 w-3.5 mr-1.5" /> Imprimir
                  </Button>
                  <Button variant="outline" onClick={doPrint} data-testid="ai-pdf-btn">
                    <FileText className="h-3.5 w-3.5 mr-1.5" /> Salvar PDF
                  </Button>
                  {canExport && (
                    <Button onClick={exportExcel} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="ai-excel-btn">
                      <FileDown className="h-3.5 w-3.5 mr-1.5" /> Excel
                    </Button>
                  )}
                </div>
              </div>

              {/* Print-only title */}
              <div className="hidden print:block">
                <h2 className="text-lg font-bold">{spec.titulo}</h2>
              </div>

              {/* Filters detected */}
              {appliedChips.length > 0 && (
                <div className="rounded-md bg-[#F9F4E7] border border-[#E8D9AE] p-3">
                  <div className="text-[10.5px] uppercase tracking-wider text-[#8A6B2B] font-medium mb-1.5 flex items-center gap-1">
                    <Filter className="h-3 w-3" /> Filtros identificados pela IA
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {appliedChips.map((c) => (
                      <Badge key={c.label} variant="outline" className="bg-white border-[#E8D9AE] text-[#4A5550]">
                        <span className="font-medium mr-1">{c.label}:</span> {c.value}
                      </Badge>
                    ))}
                    <Badge variant="outline" className="bg-white border-[#E8D9AE] text-[#4A5550]">
                      Tabelas consultadas: {(report.result.tables_queried || []).join(", ") || "—"}
                    </Badge>
                  </div>
                </div>
              )}

              {/* Narrative */}
              {report.narrative && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-4">
                  <div className="text-[10.5px] uppercase tracking-wider text-emerald-800 font-medium mb-1 flex items-center gap-1">
                    <BookOpen className="h-3 w-3" /> Análise executiva (IA)
                  </div>
                  <p className="text-[13px] leading-relaxed text-foreground/90 whitespace-pre-wrap">{report.narrative}</p>
                </div>
              )}

              {/* Stats */}
              {Object.keys(stats).length > 0 && (
                <Tabs defaultValue="tabela" className="w-full no-print">
                  <TabsList>
                    <TabsTrigger value="tabela" data-testid="tab-result-tabela">Tabela</TabsTrigger>
                    <TabsTrigger value="resumo" data-testid="tab-result-resumo">Resumo</TabsTrigger>
                  </TabsList>
                  <TabsContent value="tabela" className="m-0 mt-3">
                    <ReportTable cols={cols} rows={rows} />
                  </TabsContent>
                  <TabsContent value="resumo" className="m-0 mt-3">
                    <StatsBlock stats={stats} />
                  </TabsContent>
                </Tabs>
              )}

              {/* Print-only content: always show table */}
              <div className="hidden print:block">
                <ReportTable cols={cols} rows={rows} print />
              </div>

              {/* Print footer */}
              <div className="hidden print:block mt-4 pt-3 border-t border-black/20 text-[10px] text-black/60 flex justify-between">
                <span>TheoKratos RH · Grupo ALCON</span>
                <span>Confidencial · Uso interno</span>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: history */}
        <div className="space-y-4 no-print">
          <div className="rounded-lg border border-border bg-white p-5">
            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-[#234935]" />
              <h3 className="font-display text-lg font-semibold">Histórico</h3>
            </div>
            <div className="text-[11.5px] text-muted-foreground mt-0.5">Relatórios gerados recentemente</div>
            <div className="mt-3 space-y-2 max-h-[560px] overflow-y-auto pr-1">
              {history.length === 0 && <div className="text-[12.5px] text-muted-foreground text-center py-6">Sem histórico ainda.</div>}
              {history.map((h) => (
                <button
                  key={h.id}
                  onClick={() => openFromHistory(h.id)}
                  data-testid={`hist-${h.id}`}
                  className="w-full text-left rounded-md border border-border p-3 hover:bg-secondary/40 transition-colors duration-200"
                >
                  <div className="text-[12.5px] font-medium line-clamp-2">{h.prompt}</div>
                  <div className="mt-1 flex items-center gap-2 text-[10.5px] text-muted-foreground">
                    <Badge variant="outline" className="h-4 px-1 text-[10px]">{h.entity}</Badge>
                    <span>· {h.total} reg</span>
                    <span>· {fmtDateTime(h.at)}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Print CSS */}
      <style>{`
        @media print {
          body { background: white !important; }
          .no-print, header, aside, .rh-sidebar-link { display: none !important; }
          .print-area {
            border: none !important; box-shadow: none !important;
            max-width: 100% !important; padding: 0 !important;
          }
          main { padding: 0 !important; }
          .rh-row, table, tr, td, th { break-inside: avoid; }
          @page { margin: 1.2cm; size: A4; }
        }
      `}</style>
    </div>
  );
}

function ReportTable({ cols, rows, print = false }) {
  if (!rows?.length) return <div className="text-center text-muted-foreground py-6">Sem dados para exibir.</div>;
  return (
    <div className={print ? "overflow-visible" : "rounded-md border border-border overflow-x-auto"}>
      <Table>
        <TableHeader>
          <TableRow>
            {cols.map((c) => (
              <TableHead key={c} className="text-[11px] uppercase tracking-wider">{COLUMN_LABELS[c] || c}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={r.id || i} className="rh-row">
              {cols.map((c) => (
                <TableCell key={c} className={`text-[12.5px] ${["salario", "salario_base"].includes(c) ? "font-mono tabular-nums" : ""}`}>
                  {formatCell(c, r[c])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function StatsBlock({ stats }) {
  const entries = Object.entries(stats).filter(([k]) => typeof stats[k] !== "object");
  const porStatus = stats.por_status;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {entries.map(([k, v]) => (
          <div key={k} className="rounded-md border border-border p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{prettyLabel(k)}</div>
            <div className="font-display text-xl font-semibold mt-0.5">{formatStatValue(k, v)}</div>
          </div>
        ))}
      </div>
      {porStatus && (
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-medium mb-2">Distribuição por status</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(porStatus).map(([st, n]) => (
              <Badge key={st} variant="outline">{st}: <b className="ml-1">{n}</b></Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function prettyLabel(k) {
  const map = {
    total_registros: "Total registros",
    salario_medio: "Salário médio",
    salario_min: "Salário mín.",
    salario_max: "Salário máx.",
    quadro_aprovado_total: "Quadro aprovado",
    quadro_atual_total: "Quadro atual",
    ocupacao_media: "Ocupação média",
  };
  return map[k] || k.replace(/_/g, " ");
}

function formatStatValue(k, v) {
  if (["salario_medio", "salario_min", "salario_max"].includes(k)) return BRL(v);
  if (k === "ocupacao_media") return `${v}%`;
  return v;
}
